import React from 'react';
import { useExperiments } from '@wix/yoshi-flow-editor';
import { connect } from '../../../../common/components/runtime-context';
import {
  getDetails,
  isSkipEnabled,
  getMinStartNoticeDays,
  getMinRevocationNoticeDays,
  getMaxUserActions,
} from '../../selectors';
import { classes } from './SubscriptionsListItemBass.st.css';
import { PAID_PLANS_APP_DEF_ID, STORES_APP_DEF_ID } from '../../constants';
import { RootState } from '../../state';
import { ActionType, Subscription, SubscriptionStatus } from '@wix/ambassador-billing-v1-subscription/types';
import {
  getSkipsLeft,
  isInGracePeriod,
  isPendingCancellationSubscription,
  isRecurringSubscription,
  shouldShowSkipCta,
  shouldShowUnskipCta,
} from '../../domainUtils';
import { Experiments } from '../../../../Experiments';
import UpmCta from './UpmCta';
import CancelSubscriptionCta from './CancelSubscriptionCta';
import SkipSubscriptionCta from './SkipSubscriptionCta';
import CancelScheduledSkipCta from './CancelScheduledSkipCta';
import NavigateToVerticalCta from './NavigateToVerticalCta';
import PayNowCta from './PayNowCta';

type RuntimeProps = ReturnType<typeof mapRuntimeToProps>;

interface Props {
  subscription: Subscription;
}

const CallToActions = ({
  subscription,
  details,
  skipEnabled,
  minStartNoticeDays,
  minRevocationNoticeDays,
  maxUserActions,
}: Props & RuntimeProps) => {
  const { experiments } = useExperiments();
  const enableSkipSubscriptions = experiments.enabled(Experiments.SKIP_SUBSCRIPTIONS_MEMBERS_AREA);
  const isPPSubscription = subscription.origin?.appId === PAID_PLANS_APP_DEF_ID;
  const isStoresSubscription = subscription.origin?.appId === STORES_APP_DEF_ID;

  const hasBuyNewPlanStatus =
    (subscription.status &&
      [SubscriptionStatus.ENDED, SubscriptionStatus.CANCELED].indexOf(subscription.status) > -1) ||
    isPendingCancellationSubscription(subscription);
  const isRecurring = isRecurringSubscription(subscription);
  const isCancelActionAllowed = details?.allowedActions?.filter(
    (action) => action.actionType === (isRecurring ? ActionType.CYCLE_AUTO_RENEW_OFF : ActionType.CANCEL),
  ).length;
  const shouldShowPayNowCta = isInGracePeriod(subscription);
  const shouldShowUpmCta = !!details?.allowedActions?.find((action) => action.actionType === ActionType.UPM);
  const shouldShowNavigateToVerticalCta = !!((isPPSubscription || isStoresSubscription) && hasBuyNewPlanStatus);
  const shouldShowCancelSubscriptionCta = !!(isCancelActionAllowed && !hasBuyNewPlanStatus);

  const skipCtasVisible = enableSkipSubscriptions && skipEnabled && !hasBuyNewPlanStatus;
  const showUnskipCta = skipCtasVisible && shouldShowUnskipCta(subscription, minRevocationNoticeDays, new Date());
  const showSkipCta = skipCtasVisible && shouldShowSkipCta(subscription, minStartNoticeDays);
  const skipCtaDisabled = getSkipsLeft(subscription, maxUserActions) <= 0;

  return (
    <div className={classes.ctaContainer}>
      {shouldShowPayNowCta && <PayNowCta subscription={subscription} />}
      {shouldShowUpmCta && <UpmCta subscription={subscription} />}
      {shouldShowNavigateToVerticalCta && <NavigateToVerticalCta subscription={subscription} />}
      {shouldShowCancelSubscriptionCta && <CancelSubscriptionCta subscription={subscription} />}
      {showUnskipCta && <CancelScheduledSkipCta subscription={subscription} />}
      {showSkipCta && <SkipSubscriptionCta subscription={subscription} disabled={skipCtaDisabled} />}
    </div>
  );
};

const mapRuntimeToProps = (state: RootState, { subscription }: Props) => {
  const subscriptionId: string = subscription.id!;
  return {
    details: getDetails(state, subscriptionId),
    skipEnabled: isSkipEnabled(state, subscription),
    minStartNoticeDays: getMinStartNoticeDays(state, subscription),
    minRevocationNoticeDays: getMinRevocationNoticeDays(state, subscription),
    maxUserActions: getMaxUserActions(state, subscription),
  };
};

export default connect(mapRuntimeToProps)(CallToActions);
